import { useQuery } from '@tanstack/react-query'
import { useId, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { PriceInput } from '../inputs.js'
import { Input, Select, Textarea } from '../ui.js'
import { servicesTotal, type RuleDraft } from './rule.js'

const PRESET_MONTHS = [1, 3, 6, 12] as const

/**
 * The body of "Make this job repeat": how often, counted from what, which
 * services at what price, who is booked, when it ends, and what every visit
 * carries. Drawn by the job card's popup and the agreement's Add service, so
 * both ask the same questions in the same order. Decision 96.
 */
export function RuleFields({
  draft,
  onChange,
  showNotes = true,
}: {
  draft: RuleDraft
  onChange: (patch: Partial<RuleDraft>) => void
  showNotes?: boolean
}) {
  const trpc = useTRPC()
  const id = useId()
  const catalogue = useQuery(trpc.forms.serviceTypes.queryOptions())
  const people = useQuery(trpc.users.schedulable.queryOptions())

  const preset =
    draft.intervalUnit === 'MONTH' &&
    (PRESET_MONTHS as readonly number[]).includes(draft.intervalCount)
      ? String(draft.intervalCount)
      : 'custom'

  const chosen = new Set(draft.services.map((service) => service.serviceTypeId))
  const addable = (catalogue.data ?? []).filter((service) => !chosen.has(service.id))

  return (
    <div className="flex flex-col gap-5">
      <Row label="Every">
        <Choice
          label="Every"
          value={preset}
          options={[
            ...PRESET_MONTHS.map((months) => ({
              value: String(months),
              label: months === 1 ? '1 month' : `${months} months`,
            })),
            { value: 'custom', label: 'Custom' },
          ]}
          onChange={(value) =>
            value === 'custom'
              ? onChange({ intervalUnit: 'WEEK', intervalCount: 2 })
              : onChange({ intervalUnit: 'MONTH', intervalCount: Number(value) })
          }
        />
        {preset === 'custom' ? (
          <div className="flex items-center gap-2">
            <Input
              aria-label="How many"
              type="number"
              min={1}
              max={104}
              value={draft.intervalCount}
              onChange={(event) => onChange({ intervalCount: Number(event.target.value) })}
              className="w-20"
            />
            <Select
              aria-label="Weeks or months"
              value={draft.intervalUnit}
              onChange={(event) =>
                onChange({ intervalUnit: event.target.value as RuleDraft['intervalUnit'] })
              }
              className="w-32"
            >
              <option value="WEEK">weeks</option>
              <option value="MONTH">months</option>
            </Select>
          </div>
        ) : null}
      </Row>

      <Row
        label="Counts from"
        hint={
          draft.anchor === 'DUE_DATE'
            ? 'Visits stay on their dates even when one is done late.'
            : 'The next visit moves with the day this one is actually done.'
        }
      >
        <Choice
          label="Counts from"
          value={draft.anchor}
          options={[
            { value: 'DUE_DATE', label: 'Due date' },
            { value: 'COMPLETED_DATE', label: 'Completed date' },
          ]}
          onChange={(anchor) => onChange({ anchor })}
        />
      </Row>

      <Row label="Services and price" hint="Each visit's job is created with these, at this price.">
        {draft.services.length > 0 ? (
          <ul className="flex flex-col divide-y divide-ink-100 rounded-lg ring-1 ring-ink-200 ring-inset">
            {draft.services.map((service, index) => (
              <li key={service.serviceTypeId} className="flex items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[13.5px] text-ink-900">
                  {service.name}
                </span>
                <PriceInput
                  label={`${service.name} price`}
                  value={service.priceIncTax}
                  placeholder="0.00"
                  className="w-28 text-right tabular-nums"
                  onChange={(priceIncTax) =>
                    onChange({
                      services: draft.services.map((row, at) =>
                        at === index ? { ...row, priceIncTax } : row,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  aria-label={`Remove ${service.name}`}
                  onClick={() =>
                    onChange({ services: draft.services.filter((_, at) => at !== index) })
                  }
                  className="grid size-8 place-items-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-800"
                >
                  <Icon name="close" className="size-4" />
                </button>
              </li>
            ))}
            <li className="flex items-center justify-between px-3 py-2 text-[13px]">
              <span className="text-ink-500">Each visit, inc GST</span>
              <span className="font-semibold text-ink-900 tabular-nums">
                {money(servicesTotal(draft.services))}
              </span>
            </li>
          </ul>
        ) : null}
        <Select
          aria-label="Add a service"
          value=""
          onChange={(event) => {
            const picked = addable.find((service) => service.id === event.target.value)
            if (!picked) return
            onChange({
              services: [
                ...draft.services,
                {
                  serviceTypeId: picked.id,
                  name: picked.name,
                  priceIncTax: picked.defaultPriceIncTax ?? null,
                },
              ],
            })
          }}
        >
          <option value="">{draft.services.length ? 'Add another service…' : 'Add a service…'}</option>
          {addable.map((service) => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
        </Select>
      </Row>

      <Row label="Book with" htmlFor={`${id}-book`}>
        <Select
          id={`${id}-book`}
          value={draft.bookWith === 'PERSON' ? `PERSON:${draft.bookWithUserId ?? ''}` : draft.bookWith}
          onChange={(event) => {
            const value = event.target.value
            if (value.startsWith('PERSON:')) {
              onChange({ bookWith: 'PERSON', bookWithUserId: value.slice(7) || null })
            } else {
              onChange({ bookWith: value as RuleDraft['bookWith'], bookWithUserId: null })
            }
          }}
        >
          <option value="LAST_TECH">The technician who did the last visit</option>
          <option value="UNASSIGNED">Nobody — leave it for the board</option>
          <optgroup label="Always book">
            {(people.data ?? []).map((person) => (
              <option key={person.id} value={`PERSON:${person.id}`}>
                {person.name}
              </option>
            ))}
          </optgroup>
        </Select>
      </Row>

      <Row label="Ends">
        <Choice
          label="Ends"
          value={draft.ends}
          options={[
            { value: 'NEVER', label: 'Never' },
            { value: 'AFTER_VISITS', label: 'After' },
            { value: 'ON_DATE', label: 'On a date' },
          ]}
          onChange={(ends) =>
            onChange({
              ends,
              endAfterVisits: ends === 'AFTER_VISITS' ? (draft.endAfterVisits ?? 4) : draft.endAfterVisits,
            })
          }
        />
        {draft.ends === 'AFTER_VISITS' ? (
          <div className="flex items-center gap-2 text-[13.5px] text-ink-700">
            <Input
              aria-label="Number of visits"
              type="number"
              min={1}
              max={520}
              value={draft.endAfterVisits ?? ''}
              onChange={(event) =>
                onChange({ endAfterVisits: event.target.value ? Number(event.target.value) : null })
              }
              className="w-20"
            />
            visits, counting this one
          </div>
        ) : draft.ends === 'ON_DATE' ? (
          <Input
            aria-label="Last date"
            type="date"
            value={draft.endsOn ?? ''}
            onChange={(event) => onChange({ endsOn: event.target.value || null })}
            className="w-48"
          />
        ) : null}
      </Row>

      {showNotes ? (
        <Row
          label="Carried onto every visit"
          hint="Gate codes, pets, where the bait stations are. Written into each new job's brief."
          htmlFor={`${id}-notes`}
        >
          <Textarea
            id={`${id}-notes`}
            value={draft.standingNotes}
            onChange={(event) => onChange({ standingNotes: event.target.value })}
            rows={3}
          />
        </Row>
      ) : null}
    </div>
  )
}

function Row({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string
  hint?: string
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-[12.5px] font-semibold text-ink-800">
            {label}
          </label>
        ) : (
          <span className="text-[12.5px] font-semibold text-ink-800">{label}</span>
        )}
        {hint ? <span className="text-[12px] text-ink-500">{hint}</span> : null}
      </div>
      {children}
    </div>
  )
}

/** A row of mutually exclusive pills, as radios. Selected is ink-900, not green. */
export function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-h-[var(--control-md)] rounded-lg px-3 text-[13px] font-semibold transition-colors',
              active
                ? 'bg-ink-900 text-white'
                : 'bg-white text-ink-700 ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
